import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSetAtom } from 'jotai';
import { QRCodeSVG } from 'qrcode.react';
import { Typography, Button, Paper, Stack, Box } from '@mui/material';
import PasswordRoundedIcon from '@mui/icons-material/PasswordRounded';
import { openSnackbarAdviseAtom } from '../../../Atoms';
import OtpField from '../../../Components/Inputs/OtpField';
import useAuthAPI from '../../../api/auth';
import '../../../Styles/login.scss';
import ContentError from '../../../Components/Error/ContentError';

const Setup2FAPage = function Setup2FAPage() {
  const intl = useIntl();
  const AuthAPI = useAuthAPI();
  const [otpCode, setOtpCode] = useState('');
  const [setupValue, setSetupValue] = useState(null);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const setOpenSnackbarAdvise = useSetAtom(openSnackbarAdviseAtom);

  const loadTwoFactorData = () => {
    AuthAPI.twofactorEnabled()
      .then(({ data }) => {
        setTwoFactorEnabled(data);
        if (!data) {
          AuthAPI.generateTotpSetup(
            (result) => {
              setSetupValue(result);
            },
            () => {
              setSetupValue('ERROR');
            }
          );
        }
      })
      .catch(() => {
        setOpenSnackbarAdvise({
          message: intl.messages.ERROR_SETUP_2FA,
          severity: 'error',
        });
      });
  };

  useEffect(() => {
    loadTwoFactorData();
  }, []);

  const handleTotpSetup = () => {
    AuthAPI.verifyTotp(
      {
        code: otpCode,
      },
      () => {
        setTwoFactorEnabled(true);
        setOpenSnackbarAdvise({
          message: intl.messages.SETUP_SUCCESS_2FA,
          severity: 'success',
        });
      },
      () => {
        setTwoFactorEnabled(false);
        setOpenSnackbarAdvise({
          message: intl.messages.ERROR_SETUP_2FA,
          severity: 'error',
        });
      }
    );
  };

  const handleDisable2FA = () => {
    AuthAPI.disableTwoFactor(
      () => {
        setOtpCode('');
        loadTwoFactorData();
        setOpenSnackbarAdvise({
          message: intl.messages.DISABLE_2FA_SUCCESS,
          severity: 'success',
        });
      },
      () => {
        setOpenSnackbarAdvise({
          message: intl.messages.DISABLE_2FA_ERROR,
          severity: 'error',
        });
      }
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        padding: '2vw',
      }}
    >
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
        }}
      >
        {!twoFactorEnabled && (
          <Box
            sx={{
              display: 'flex',
            }}
          >
            <Stack spacing={2}>
              <Typography
                variant="h4"
                sx={{
                  color: 'primary.main',
                  fontSize: '30px',
                }}
              >
                {intl.messages.SETUP_2FA}
              </Typography>

              <ol>
                <li
                  style={{
                    fontFamily: 'Roboto',
                    fontSize: '1.1rem',
                    marginTop: '36px',
                  }}
                >
                  {intl.messages.OPEN_AUTH_APP}
                </li>
                <li
                  style={{
                    fontFamily: 'Roboto',
                    fontSize: '1.1rem',
                    marginTop: '36px',
                  }}
                >
                  {intl.messages.SCAN_QRCODE_FROM_APP}
                </li>
                <li
                  style={{
                    fontFamily: 'Roboto',
                    fontSize: '1.1rem',
                    marginTop: '36px',
                  }}
                >
                  {intl.messages.INSERT_OTPCODE}
                </li>
              </ol>
            </Stack>
            <Stack
              sx={{
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  span: '200px',
                  marginBottom: '36px',
                  minHeight: '256px',
                }}
              >
                {setupValue && setupValue === 'ERROR' ? (
                  <Typography
                    sx={{
                      fontSize: '0.9rem',
                      color: 'greys.secondary',
                      margin: 'auto',
                    }}
                  >
                    {intl.messages.QRCODE_ERROR}
                  </Typography>
                ) : (
                  <QRCodeSVG value={setupValue?.qrCodeUri} size={256} />
                )}
              </div>
              <Typography variant="h5" sx={{ color: 'greys.title' }}>
                {intl.messages.OTP_CODE}
              </Typography>
              <OtpField
                value={otpCode}
                onChange={(value) => setOtpCode(value)}
                length={6}
                fontSize="1.5rem"
              />
              <Button
                id="enable-2fa-btn"
                color="primary"
                onClick={handleTotpSetup}
                disabled={otpCode.length < 6}
                sx={{ width: '160px' }}
              >
                {intl.messages.CONFIRM}
              </Button>
            </Stack>
          </Box>
        )}
        {twoFactorEnabled && (
          <>
            <ContentError
              icon={
                <PasswordRoundedIcon
                  sx={{ color: '#acacac', fontSize: '64px' }}
                />
              }
              text={intl.messages.TWO_FACTOR_AUTH_ENABLED}
              message={intl.messages.CLICK_ON_BTN_TO_DISABLE}
              sizes={{
                title: '36px',
                message: '24px',
              }}
            />
            <Button
              id="disable-2fa-btn"
              color="primary"
              onClick={handleDisable2FA}
              sx={{ width: '160px' }}
            >
              {intl.messages.DISABLE}
            </Button>
          </>
        )}
      </Paper>
    </div>
  );
};

export default Setup2FAPage;
